'use client';

import { createContext, type PropsWithChildren, use, useMemo } from 'react';
import type { TypographyProps } from './index';

type CopyableTypographyProps = Omit<TypographyProps, 'children' | 'className' | 'as' | 'style'>;

export const TypographyContext = createContext<CopyableTypographyProps | null>(null);

export const TypographyProvider = TypographyContext.Provider;

export function TypographyProviderReset({ children }: PropsWithChildren) {
  return <TypographyContext.Provider value={null}>{children}</TypographyContext.Provider>;
}

export function useTypographyContext() {
  return use(TypographyContext);
}

export function useMergedTypography<T extends CopyableTypographyProps | undefined = CopyableTypographyProps>(
  defaultProps: T,
  preference: 'inherited' | 'current' = 'inherited',
): T {
  const inheritedTypography = useTypographyContext();

  return useMemo(
    () =>
      preference === 'current'
        ? {
            ...inheritedTypography,
            ...defaultProps,
          }
        : {
            ...defaultProps,
            ...inheritedTypography,
          },
    [inheritedTypography, defaultProps, preference],
  );
}
